import { useState } from "react";
import { toast } from "sonner";
import { Copy, DownloadSimple } from "@phosphor-icons/react";
import { downloadPdf } from "@/lib/download";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
const STATUS = {
  active: ["Ready to redeem", "bg-[#EBF4F5] text-[#245E63]"],
  redeemed: ["Redeemed", "bg-[#EAF1EE] text-[#4A6B5D]"],
  expired: ["Expired", "bg-[#F4EFE6] text-[#8A7F76]"],
  refunded: ["Refunded", "bg-[#F4EFE6] text-[#8A7F76]"],
};

const GiftRow = ({ g, highlight }) => {
  const [busy, setBusy] = useState(false);
  const [label, cls] = STATUS[g.status] || STATUS.active;
  const download = async () => {
    setBusy(true);
    try { await downloadPdf(`/gifts/${g.code}/certificate`, `Gift certificate ${g.recipient_name}`); }
    catch { toast.error("We couldn't download the certificate. Please try again."); }
    finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard?.writeText(g.code).then(() => toast.success("Code copied"));
  return (
    <li data-testid={`gift-row-${g.code}`} className={`rounded-xl bg-white p-4 ring-1 ${highlight ? "ring-2 ring-[#E06D53]" : "ring-[#EBE4DA]"}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-serif text-xl text-[#2D2824]">For {g.recipient_name}</p>
          <p className="mt-0.5 text-xs text-[#8A7F76]">Order {g.order_number} · bought {fmt(g.created_at)} · valid until {fmt(g.expires_at)}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>{label}</span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span data-testid={`gift-code-${g.code}`} className="rounded-lg bg-[#FAF6F0] px-3 py-2 font-mono text-base tracking-wider text-[#2D2824]">{g.code}</span>
        <button onClick={copy} data-testid={`gift-copy-${g.code}`} className="flex min-h-[40px] items-center gap-1 rounded-full px-3 text-sm text-[#36858B] hover:bg-[#EBF4F5]">
          <Copy size={16} /> Copy
        </button>
        <button onClick={download} disabled={busy} data-testid={`gift-download-${g.code}`}
          className="flex min-h-[40px] items-center gap-1.5 rounded-full bg-[#E06D53] px-4 text-sm font-semibold text-white hover:bg-[#C85B43] disabled:opacity-60">
          <DownloadSimple size={16} /> {busy ? "Preparing…" : "Printable certificate"}
        </button>
      </div>
    </li>
  );
};

export const GiftList = ({ gifts, highlight }) =>
  gifts.length > 0 && (
    <section data-testid="gift-list" className="mt-12">
      <h2 className="font-serif text-2xl text-[#2D2824]">Gifts you've given</h2>
      <p className="mt-1 text-sm text-[#6E665E]">Download the certificate any time to print it or forward it.</p>
      <ul className="mt-4 space-y-3">
        {gifts.map((g) => <GiftRow key={g.code} g={g} highlight={highlight === g.code} />)}
      </ul>
    </section>
  );
